import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
} from '@openx/design-system/components/Autocomplete/Autocomplete.tsx';
import { TextField } from '@openx/design-system/components/TextField/TextField.tsx';
import { Tooltip } from '@openx/design-system/components/Tooltip/Tooltip.tsx';
import { type HTMLAttributes, type Key, type SyntheticEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOpenXAgentModalContainer } from '../Modal/OpenXAgentModalContainerContext.ts';
import { useDealSearch } from './hooks/useDealSearch.ts';
import { autocompleteSx, hintSx, listboxSx, optionContentSx, rootSx } from './styles.ts';
import { getDealId, getDealKey, getDealLabel, isSameDeal, skipClientFiltering } from './utils/dealOptions.ts';
import type { DealOption } from './utils/types.ts';

type DealSelectProps = {
  onSelect: (deal: DealOption) => void;
};

export const DealSelect = ({ onSelect }: DealSelectProps) => {
  const { t } = useTranslation();
  const [phrase, setPhrase] = useState('');
  const [isListOpen, setIsListOpen] = useState(false);
  const { deals, isLoading } = useDealSearch(phrase);

  const modalContainer = useOpenXAgentModalContainer();
  const inputLabel = t('Select or Search for Deals');

  const handleInputChange = useCallback((_event: SyntheticEvent, value: string) => setPhrase(value), []);
  const handleOpen = useCallback(() => setIsListOpen(true), []);
  const handleClose = useCallback(() => setIsListOpen(false), []);

  const handleChange = useCallback(
    (_event: SyntheticEvent, deal: DealOption | null) => {
      if (deal) onSelect(deal);
    },
    [onSelect]
  );

  const renderDealOption = useCallback(
    ({ key, ...props }: HTMLAttributes<HTMLLIElement> & { key: Key }, deal: DealOption) => (
      <Box component="li" key={key} {...props}>
        <Box sx={optionContentSx}>
          <Typography variant="body1">{deal.name}</Typography>
          <Typography variant="captionSm" color="text.secondary">
            {t('ID: {dealId}', { dealId: getDealId(deal) })}
          </Typography>
        </Box>
      </Box>
    ),
    [t]
  );

  // the design-system TextField has a single (medium) size, so Autocomplete's size param is dropped
  const renderInput = useCallback(
    ({ size: _size, ...params }: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        placeholder={inputLabel}
        slotProps={{ ...params.slotProps, htmlInput: { ...params.slotProps.htmlInput, 'aria-label': inputLabel } }}
      />
    ),
    [inputLabel]
  );

  return (
    <Box data-test="openx-agent-deal-select" sx={rootSx}>
      <Tooltip
        title={t('Select your Deal ID here')}
        open={!isListOpen}
        arrow
        describeChild
        placement="top"
        slotProps={{ popper: { container: modalContainer }, tooltip: { sx: hintSx } }}
      >
        <Autocomplete<DealOption>
          fullWidth
          options={deals}
          loading={isLoading}
          noOptionsText={t('No deals found')}
          filterOptions={skipClientFiltering}
          getOptionLabel={getDealLabel}
          getOptionKey={getDealKey}
          isOptionEqualToValue={isSameDeal}
          onInputChange={handleInputChange}
          onChange={handleChange}
          onOpen={handleOpen}
          onClose={handleClose}
          renderOption={renderDealOption}
          renderInput={renderInput}
          slotProps={{ popper: { placement: 'top-start', container: modalContainer }, listbox: { sx: listboxSx } }}
          sx={autocompleteSx}
        />
      </Tooltip>
    </Box>
  );
};
